<template lang="pug">
div(:class="isMobileScreen ? 'pt-4 px-4 pb-4 mobile-screen' : 'pt-6 px-8 pb-8'")
  //- Header section
  .d-flex.align-center(:class="isMobileScreen ? 'justify-end' : 'justify-space-between'")
    .yz-heading-5.neutral700--text(v-if="!isMobileScreen") Services
    hq-branch-dropdown(v-model="branchFilter" :class="isMobileScreen ? '' : 'px-5'")

  //- Content card
  yz-card.mt-4(:class="isMobileScreen ? 'px-3 py-3' : 'px-5 py-4'")
    //- Desktop: Tabs and actions row
    template(v-if="!isMobileScreen")
      .d-flex.justify-space-between.align-center
        yz-tabs(v-model="activeTab" type="line")
          yz-tab(label="All")
          yz-tab(label="Services")
          yz-tab(label="Procedure")
          yz-tab(label="Investigation")
          yz-tab(label="Archived")
        .d-flex.align-center.gap-2
          yz-search-btn(v-model="searchText" size="lg" @search="onSearch")
          customize-column-menu(type="hq_services" :show-tooltip="false" icon-size="21")
          yz-btn(label="Add service" size="lg" @click="onAddService")

    //- Mobile: Scrollable tabs and compact actions
    template(v-else)
      .d-flex.align-center.justify-space-between.mb-2
        yz-search-btn(v-model="searchText" size="lg" @search="onSearch")
        yz-btn(icon-only icon="$yzi-plus" size="lg" @click="onAddService")
      .overflow-x-auto
        .d-flex.align-center(style="width: max-content;")
          div(v-for="tab in tabItems" :key="tab.id")
            .px-3.py-2.cursor-pointer(@click="activeTab = tab.id")
              span.yz-medium-body-sm(:class="activeTab === tab.id ? 'neutral700--text' : 'neutral500--text'") {{ tab.label }}
            .bar-active(v-if="activeTab === tab.id")

    yz-divider.my-0

    //- Filter row
    services-filter-row(v-if="isInitialized" ref="filterRow" :filters.sync="filters" @reset="onResetFilters" @update:filters="onFiltersChange")

    //- Table
    services-table(v-if="isInitialized" ref="servicesTable" :is-mobile="isMobileScreen" :active-tab="activeTab" :search-text="searchText" :filters="filters" :location-filter="branchFilterIds" @edit-item="onEditItem" @delete-item="onDeleteItem")

  //- Delete service dialog
  delete-service-dialog(:open.sync="showDeleteDialog" :service="selectedService" is-hq @deleted="onServiceDeleted" @closed="onDeleteDialogClosed")
</template>

<script>
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import ServicesFilterRow from './ServicesFilterRow.vue'
import ServicesTable from './ServicesTable.vue'
import HqBranchDropdown from '@/components/hq/inventory/HqBranchDropdown.vue'
import DeleteServiceDialog from '@/components/clinic/dialogs/DeleteServiceDialog.vue'

export default {
  components: {
    CustomizeColumnMenu,
    ServicesFilterRow,
    ServicesTable,
    HqBranchDropdown,
    DeleteServiceDialog
  },
  data () {
    return {
      activeTab: 0,
      searchText: '',
      filters: [],
      branchFilter: null,
      isInitialized: false,
      showDeleteDialog: false,
      selectedService: null,
      tabNameToIndex: {
        all: 0,
        services: 1,
        procedure: 2,
        investigation: 3,
        archived: 4
      },
      tabIndexToName: ['all', 'services', 'procedure', 'investigation', 'archived']
    }
  },
  computed: {
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    branchFilterIds () {
      return this.branchFilter ? this.branchFilter.map(b => b.id) : null
    },
    tabItems () {
      return [
        { id: 0, label: 'All' },
        { id: 1, label: 'Services' },
        { id: 2, label: 'Procedure' },
        { id: 3, label: 'Investigation' },
        { id: 4, label: 'Archived' }
      ]
    }
  },
  watch: {
    activeTab (newTab) {
      this.updateTabQuery(newTab)
    },
    '$route.query.tab': {
      handler (newTabName) {
        if (newTabName) {
          const tabIndex = this.tabNameToIndex[newTabName]
          if (tabIndex !== undefined && tabIndex !== this.activeTab) {
            this.activeTab = tabIndex
          }
        }
      }
    },
    '$route.query.search': {
      handler (newSearch) {
        const searchValue = newSearch || ''
        if (this.searchText !== searchValue) {
          this.searchText = searchValue
        }
      }
    },
    '$route.query.filters': {
      handler (newFilters) {
        const decoded = this.deserializeFilters(newFilters)
        if (JSON.stringify(this.filters) !== JSON.stringify(decoded)) {
          this.filters = decoded
        }
      }
    }
  },
  created () {
    this.initializeStateFromRoute()
    this.isInitialized = true
  },
  methods: {
    initializeStateFromRoute () {
      const { tab, search, filters } = this.$route.query

      if (tab && this.tabNameToIndex[tab] !== undefined) {
        this.activeTab = this.tabNameToIndex[tab]
      } else {
        this.updateTabQuery(0)
      }

      if (search) {
        this.searchText = search
      }

      if (filters) {
        this.filters = this.deserializeFilters(filters)
      }
    },
    updateTabQuery (tabIndex) {
      const tabName = this.tabIndexToName[tabIndex]
      if (tabName && this.$route.query.tab !== tabName) {
        this.$router.replace({
          query: { ...this.$route.query, tab: tabName }
        }).catch(() => {})
      }
    },
    updateSearchQuery (search) {
      const newQuery = { ...this.$route.query }
      if (search) {
        newQuery.search = search
      } else {
        delete newQuery.search
      }
      if (this.$route.query.search !== newQuery.search) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    },
    updateFiltersQuery (filters) {
      const newQuery = { ...this.$route.query }
      const serialized = this.serializeFilters(filters)
      if (serialized) {
        newQuery.filters = serialized
      } else {
        delete newQuery.filters
      }
      if (this.$route.query.filters !== newQuery.filters) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    },
    serializeFilters (filters) {
      if (!filters || filters.length === 0) {
        return null
      }
      try {
        const json = JSON.stringify(filters)
        return btoa(encodeURIComponent(json))
      } catch (error) {
        console.error('Error serializing filters:', error)
        return null
      }
    },
    deserializeFilters (base64String) {
      if (!base64String) {
        return []
      }
      try {
        const json = decodeURIComponent(atob(base64String))
        return JSON.parse(json)
      } catch (error) {
        console.error('Error deserializing filters:', error)
        return []
      }
    },
    onSearch (value) {
      this.searchText = value
      this.updateSearchQuery(value)
      this.$refs.servicesTable.currentPage = 1
      this.$refs.servicesTable.fetchServices()
    },
    onResetFilters () {
      this.filters = []
      this.updateFiltersQuery([])
    },
    onFiltersChange (newFilters) {
      this.filters = newFilters
      this.updateFiltersQuery(newFilters)
    },
    onAddService () {
      this.$router.push({ name: 'HQNewService' })
    },
    onEditItem (item) {
      this.$router.push({ name: 'HQEditService', params: { id: item.id } })
    },
    onDeleteItem (item) {
      this.selectedService = item
      this.showDeleteDialog = true
    },
    onServiceDeleted () {
      this.selectedService = null
      this.$refs.servicesTable.fetchServices()
    },
    onDeleteDialogClosed () {
      this.selectedService = null
    }
  }
}
</script>

<style lang="scss" scoped>
.overflow-x-auto {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { display: none; }
  scrollbar-width: none;
}
.bar-active {
  height: 2px;
  width: 100%;
  margin: auto;
  margin-top: -2px;
  background-color: #333;
}
</style>
